import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackNavClick, trackCTAClick, trackPhoneCall, trackCallBookingStarted } from "@/lib/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { BOOK_A_CALL_URL, scrollToContactForm } from "@/lib/cta";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import logo from "@/assets/zoom-logo-blue.png?format=webp";
import { caseStudies } from "@/data/caseStudies";

const workIndustries = ["Luxury", "B2B", "Tech", "Hospitality"] as const;
const clientNameBySlug: Record<string, string> = {
  "atlantic-airways-performance-marketing": "Atlantic Airways",
  "tinder-expansion-strategy": "Tinder",
  "bumble-spain-market-expansion": "Bumble",
  "uber-eats-barcelona-launch": "Uber Eats",
  "peroni-brand-activation": "Peroni",
  "ruinart-sensoria": "Ruinart",
  "leuchtturm1917-community-building": "Leuchtturm1917",
  "guardian-glass-demand-generation": "Guardian Glass",
  "sato-europe-agency-of-record": "SATO Europe",
  "ria-money-transfer-campaign": "Ria Money Transfer",
};
const workGroups = workIndustries.map((industry) => ({
  heading: industry,
  items: caseStudies
    .filter((s) => s.industry === industry)
    .slice(0, 4)
    .map((s) => ({ label: clientNameBySlug[s.slug] ?? s.title, href: `/work/${s.slug}` })),
})).filter((g) => g.items.length > 0);

const PHONE_DISPLAY = "+44 (0) 203 488 3090";
const PHONE_HREF = "tel:+442034883090";

type ServiceItem = { label: string; href: string; description: string };
type ServiceGroup = { heading: string; items: ServiceItem[] };

const serviceGroups: ServiceGroup[] = [
  {
    heading: "Strategy",
    items: [
      { label: "Brand Strategy", href: "/services/brand-strategy", description: "Positioning, narrative and identity built to compound over time." },
      { label: "Superintelligence Readiness", href: "/services/superintelligence-readiness", description: "Audit your stack, workflows and content for the superintelligence-first buyer." },
      { label: "Ongoing Support", href: "/services/ongoing-support", description: "Fractional CMO oversight, always-on strategy and reporting." },
    ],
  },
  {
    heading: "Creative",
    items: [
      { label: "Content Creation", href: "/services/content-creation", description: "Editorial, video and social content built around a single narrative." },
      { label: "Creative Execution", href: "/services/creative-execution", description: "Campaigns and creative that ship on brief, on brand, on time." },
      { label: "Graphic Design", href: "/services/graphic-design", description: "Systematised design across brand, product and campaign surfaces." },
      { label: "Influencer Marketing", href: "/services/influencer-marketing", description: "Credible creator partnerships with measurable commercial outcomes." },
    ],
  },
  {
    heading: "Growth",
    items: [
      { label: "Paid Media", href: "/services/paid-media", description: "Google, Meta, LinkedIn and programmatic run for pipeline, not vanity." },
      { label: "Social Media Marketing", href: "/services/social-media-marketing", description: "Owned social that earns attention and moves buyers down the funnel." },
      { label: "Web Development", href: "/services/web-development", description: "Fast, conversion-first sites built on modern, measurable foundations." },
    ],
  },
];

const clientGroups = [
  {
    heading: "By industry",
    items: [
      { label: "All clients", href: "/who-we-work-with" },
      { label: "Hospitality", href: "/industries/hospitality" },
      { label: "Luxury", href: "/industries/luxury" },
      { label: "B2B", href: "/industries/b2b" },
      { label: "Tech", href: "/industries/tech" },
      { label: "Aesthetics", href: "/industries/aesthetics" },
    ],
  },
  {
    heading: "By size",
    items: [
      { label: "Startups", href: "/for-startups" },
      { label: "Small businesses", href: "/for-small-businesses" },
      { label: "Medium businesses", href: "/for-medium-businesses" },
      { label: "Enterprises", href: "/for-enterprises" },
    ],
  },
  {
    heading: "By role",
    items: [
      { label: "Who we report to", href: "/who-we-report-to" },
      { label: "CEOs", href: "/for-ceos" },
      { label: "CMOs", href: "/for-cmos" },
      { label: "Heads of Marketing", href: "/for-heads-of-marketing" },
      { label: "Heads of Growth", href: "/for-heads-of-growth" },
      { label: "COOs", href: "/for-coos" },
      { label: "CROs", href: "/for-cros" },
    ],
  },
];

const insightsGroup = {
  heading: "Insights",
  items: [
    { label: "Articles", href: "/insights", description: "Long-form writing on brand, growth and superintelligence." },
    { label: "Marketing Pulse", href: "/insights/pulse", description: "Live feed of the signals shaping marketing this week.", live: true },
    { label: "Superintelligence Advantage", href: "/superintelligence-advantage", description: "How we're using superintelligence, and how you can too." },
  ],
};

const flatLinks: { label: string; href: string }[] = [
  { label: "About", href: "/about" },
  { label: "Careers", href: "/careers" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";

  const handleBookCall = (loc: "header" | "header-mobile") => {
    trackCTAClick("Book a call", loc);
    trackCallBookingStarted(loc === "header-mobile" ? "nav" : "nav");
    setIsMobileMenuOpen(false);
  };

  const handleContactUs = (loc: "header" | "header-mobile") => {
    trackCTAClick("Contact us", loc);
    if (isHome) {
      document.getElementById("conversation")?.scrollIntoView({ behavior: "smooth" });
    } else {
      const el = document.getElementById("page-contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else scrollToContactForm();
    }
    setIsMobileMenuOpen(false);
  };

  const servicesActive = location.pathname.startsWith("/services");
  const clientsActive =
    location.pathname.startsWith("/who-we-work-with") ||
    location.pathname.startsWith("/who-we-report-to") ||
    location.pathname.startsWith("/for-") ||
    location.pathname.startsWith("/industries/");
  const workActive = location.pathname.startsWith("/work");
  const insightsActive =
    location.pathname.startsWith("/insights") || location.pathname.startsWith("/superintelligence-advantage");

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
      >
        Skip to content
      </a>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled ? "bg-background border-b border-foreground" : "bg-transparent"
        )}
      >
        <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
          <nav className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center -ml-1">
              <img
                src={logo}
                alt="The Zoom Agency"
                className="h-7 w-auto object-contain transition-opacity"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-6">
              <NavigationMenu>
                <NavigationMenuList className="gap-2">
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={cn(
                        "bg-transparent text-[15px] font-medium h-9 px-3",
                        servicesActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[560px] p-5 grid grid-cols-3 gap-5">
                        {serviceGroups.map((group) => (
                          <div key={group.heading}>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2.5 font-medium">
                              {group.heading}
                            </div>
                            <ul className="space-y-1.5">
                              {group.items.map((item) => (
                                <li key={item.href}>
                                  <Link
                                    to={item.href}
                                    onClick={() => trackNavClick(item.label)}
                                    className="block text-sm text-foreground/80 hover:text-primary transition-colors"
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        <div className="col-span-3 pt-3 mt-1 border-t border-border/60">
                          <Link
                            to="/services"
                            onClick={() => trackNavClick("View all services")}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            View all services →
                          </Link>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={cn(
                        "bg-transparent text-[15px] font-medium h-9 px-3",
                        clientsActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Clients
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[640px] p-6 grid grid-cols-3 gap-6">
                        {clientGroups.map((group) => (
                          <div key={group.heading}>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-medium">
                              {group.heading}
                            </div>
                            <ul className="space-y-2">
                              {group.items.map((item) => (
                                <li key={item.href}>
                                  <Link
                                    to={item.href}
                                    onClick={() => trackNavClick(item.label)}
                                    className="text-sm text-foreground/80 hover:text-primary transition-colors"
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={cn(
                        "bg-transparent text-[15px] font-medium h-9 px-3",
                        workActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Work
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[640px] p-5 grid grid-cols-4 gap-5">
                        {workGroups.map((group) => (
                          <div key={group.heading}>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2.5 font-medium">
                              {group.heading}
                            </div>
                            <ul className="space-y-1.5">
                              {group.items.map((item) => (
                                <li key={item.href}>
                                  <Link
                                    to={item.href}
                                    onClick={() => trackNavClick(item.label)}
                                    className="block text-sm text-foreground/80 hover:text-primary transition-colors leading-snug"
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        <div className="col-span-4 pt-3 mt-1 border-t border-border/60">
                          <Link
                            to="/work"
                            onClick={() => trackNavClick("View all work")}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            View all work →
                          </Link>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      className={cn(
                        "bg-transparent text-[15px] font-medium h-9 px-3",
                        insightsActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Insights
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[360px] p-5">
                        <ul className="space-y-1">
                          {insightsGroup.items.map((item) => (
                            <li key={item.href}>
                              <Link
                                to={item.href}
                                onClick={() => trackNavClick(item.label)}
                                className="block rounded-md px-3 py-2.5 hover:bg-surface-recessed transition-colors group"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                    {item.label}
                                  </span>
                                  {item.live && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary">
                                      <span className="relative inline-flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                                      </span>
                                      Live
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                                    {item.description}
                                  </p>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {flatLinks.map((link) => {
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => trackNavClick(link.label)}
                    className={cn(
                      "text-[15px] font-medium transition-colors link-underline",
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}

              <a
                href={PHONE_HREF}
                onClick={() => trackPhoneCall("header")}
                className="hidden xl:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Phone className="h-3.5 w-3.5" />
                {PHONE_DISPLAY}
              </a>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleContactUs("header")}
                className="text-foreground hover:text-primary"
              >
                Contact us
              </Button>

              <Button
                asChild
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <a
                  href={BOOK_A_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleBookCall("header")}
                >
                  Book a call
                </a>
              </Button>
            </div>

            <button
              className="lg:hidden p-2 text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="lg:hidden fixed inset-0 top-16 bg-background/60 backdrop-blur-sm z-40"
                  onClick={() => setIsMobileMenuOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border z-50 max-h-[calc(100vh-4rem)] overflow-y-auto"
                >
                  <div className="w-full max-w-[1400px] mx-auto px-6 py-4 flex flex-col">
                    <Accordion type="multiple" className="w-full">
                      <AccordionItem value="services" className="border-border/60">
                        <AccordionTrigger className="text-lg font-medium py-3 hover:no-underline">
                          Services
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col gap-4 pb-2">
                            {serviceGroups.map((group) => (
                              <div key={group.heading}>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                  {group.heading}
                                </div>
                                <ul className="space-y-2 pl-1">
                                  {group.items.map((item) => (
                                    <li key={item.href}>
                                      <Link
                                        to={item.href}
                                        onClick={() => trackNavClick(item.label)}
                                        className="text-base text-foreground/85 active:text-primary block py-1"
                                      >
                                        {item.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <Link
                              to="/services"
                              onClick={() => trackNavClick("View all services")}
                              className="text-sm font-medium text-primary py-2"
                            >
                              View all services →
                            </Link>
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="clients" className="border-border/60">
                        <AccordionTrigger className="text-lg font-medium py-3 hover:no-underline">
                          Clients
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col gap-4 pb-2">
                            {clientGroups.map((group) => (
                              <div key={group.heading}>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                  {group.heading}
                                </div>
                                <ul className="space-y-2 pl-1">
                                  {group.items.map((item) => (
                                    <li key={item.href}>
                                      <Link
                                        to={item.href}
                                        onClick={() => trackNavClick(item.label)}
                                        className="text-base text-foreground/85 active:text-primary block py-1"
                                      >
                                        {item.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="work" className="border-border/60">
                        <AccordionTrigger className="text-lg font-medium py-3 hover:no-underline">
                          Work
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col gap-4 pb-2">
                            {workGroups.map((group) => (
                              <div key={group.heading}>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                  {group.heading}
                                </div>
                                <ul className="space-y-2 pl-1">
                                  {group.items.map((item) => (
                                    <li key={item.href}>
                                      <Link
                                        to={item.href}
                                        onClick={() => trackNavClick(item.label)}
                                        className="text-base text-foreground/85 active:text-primary block py-1"
                                      >
                                        {item.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <Link
                              to="/work"
                              onClick={() => trackNavClick("View all work")}
                              className="text-sm font-medium text-primary py-2"
                            >
                              View all work →
                            </Link>
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="insights" className="border-border/60">
                        <AccordionTrigger className="text-lg font-medium py-3 hover:no-underline">
                          Insights
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-1 pb-2">
                            {insightsGroup.items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  to={item.href}
                                  onClick={() => trackNavClick(item.label)}
                                  className="text-base text-foreground/85 active:text-primary flex items-center gap-2 py-1"
                                >
                                  {item.label}
                                  {item.live && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary">
                                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                      Live
                                    </span>
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <div className="flex flex-col gap-1 mt-1">
                      {flatLinks.map((link) => {
                        const isActive = location.pathname === link.href;
                        return (
                          <Link
                            key={link.href}
                            to={link.href}
                            onClick={() => trackNavClick(link.label)}
                            className={cn(
                              "text-lg font-medium py-3 min-h-[48px] flex items-center border-b border-border/60",
                              isActive ? "text-foreground" : "text-muted-foreground active:text-foreground"
                            )}
                          >
                            {link.label}
                          </Link>
                        );
                      })}
                    </div>

                    <a
                      href={PHONE_HREF}
                      onClick={() => trackPhoneCall("header-mobile")}
                      className="mt-4 flex items-center gap-2 text-base text-foreground hover:text-primary py-2"
                    >
                      <Phone className="h-4 w-4" />
                      {PHONE_DISPLAY}
                    </a>

                    <Button
                      asChild
                      variant="default"
                      className="mt-3 w-full min-h-[48px]"
                    >
                      <a
                        href={BOOK_A_CALL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleBookCall("header-mobile")}
                      >
                        Book a call
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      className="mt-2 mb-2 w-full min-h-[48px]"
                      onClick={() => handleContactUs("header-mobile")}
                    >
                      Contact us
                    </Button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </header>
    </>
  );
}
